import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Permission, Res, DeliveryNoteDTO, PrintPreviewDTO, BuyerCustomerDTO } from 'core/api';
import { BackActionButton, Content, Footer, Header, PosScreen, useClickWithFeedback } from 'pos/ui';
import * as NotePreconditions from './Preconditions';
import { DeliveryNoteFacade, PrintFacade } from 'pos/facades';
import { SaleModuleNextAction } from '../helpers/SaleModuleNextAction';
import { BarcodeEncoder } from 'pos/services';
import getTableCompanions from '../main/ticket/getTableCompanions';
import * as DeliveryNoteStore from './DeliveryNoteStore';
import { Message, Modal, useOverlayExecutor } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BarcodeReader, ChakraUtils, SvgIcon } from 'core/ui-next';
import { CheckBoxMenuItemDef } from 'core/ui-next/controls/CheckBoxMenu';
import { Events } from 'core/infrastructure';
import { Close } from 'core/img';
import { Box, HStack, VStack, Text, TableScrollArea, Table, TableCellProps, Separator, Menu, Button, Portal, Checkbox } from '@chakra-ui/react';
import z from 'zod';

// Necesito el model para poder formatear correctamente Serie/Number y para poder tener el CIF
// como columna propia (en el DTO viaja dentro de Customer y las columnas se indexan por campo del model).
export type DeliveryNoteModel = DeliveryNoteDTO & { SerieAndNumber: string, Cif: string };
const toModel = (note: DeliveryNoteDTO) => ({ ...note, SerieAndNumber: UI.Format.asDocumentNumber(note.Serie, note.Number), Cif: note.Customer.Cif });

// Comprueba si un conjunto de albaranes se puede facturar junto (una única factura o abono para todos).
// Replica las condiciones que evalúa NotePreconditions.canBeInvoiced() sin sus efectos: ese helper muestra
// un Message por cada condición que falla, así que no se puede usar durante el render (abriría diálogos
// al ir marcando filas). Lo usamos sólo para decidir si hay previsualización que mostrar; la validación
// de verdad, con sus mensajes, la siguen haciendo los comandos de cobrar e imprimir proforma.
const canBeInvoicedTogether = (notes: DeliveryNoteModel[]): boolean => {

	if (notes.length <= 1)
		return true;

	// Todos los albaranes tienen que ser del mismo cliente y del mismo tipo de ticket.
	if (new Set(notes.map(x => x.Customer.Id)).size > 1)
		return false;

	if (new Set(notes.map(x => x.TicketType)).size > 1)
		return false;

	// Los albaranes con recargo por servicio tienen que compartir el tipo de recargo y su IVA.
	const serviceChargeRates = new Set(notes.filter(x => x.ServiceChargeGross !== 0).map(x => `${x.AppliedServiceChargeSurchargeRate}|${x.AppliedServiceChargeVatRate}`));
	if (serviceChargeRates.size > 1)
		return false;

	// Un abono no puede quedar con importe pendiente de cobro y una factura no puede estar sobrepagada.
	const isRefund = notes.sum(x => x.NetAmount) < 0;
	const pendingAmount = notes.sum(x => x.PendingAmount);

	if (isRefund && pendingAmount > 0)
		return false;

	if (!isRefund && pendingAmount < 0)
		return false;

	return true;
};


type Props = { initialDeliveryNotes: DeliveryNoteModel[] } & Modal.Actions<SaleModuleNextAction>;

const stateSchema = z.object({
	searchText: z.string(),
});

type FormState = z.infer<typeof stateSchema>;

function ShowDeliveryNotesScreen(props: Props) {

	const { initialDeliveryNotes, cancel, close } = props;
	const { execute } = useOverlayExecutor();

	//State
	const [allDeliveryNotes, setAllDeliveryNotes] = React.useState<DeliveryNoteModel[]>(initialDeliveryNotes);
	const [deliveryNotes, setDeliveryNotes] = React.useState<DeliveryNoteModel[]>(initialDeliveryNotes);
	const [selectedDeliveryNotes, setSelectedDeliveryNotes] = React.useState<DeliveryNoteModel[]>([]);
	const [preview, setPreview] = React.useState<Nullable<PrintPreviewDTO>>(null);
	const [selectedColumn, setSelectedColumn] = React.useState<Nullable<ChakraUtils.ColumnDef<DeliveryNoteModel>>>(null);
	const [columnSortDirection, setColumnSortDirection] = React.useState<Nullable<ChakraUtils.SortDirection>>(null);

	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input, CheckBoxMenu } = useControlsFor<FormState>();

	const columnsDef = buildColumnsDef();

	const initialFormState: FormState = {
		searchText: '',
	};

	// Refs con la selección y el último texto buscado "vivos", para que `reload` no dependa del estado
	// y no se re-cree en cada cambio (equivalente a leer this.state en la clase). Así la suscripción a
	// los eventos de dominio se hace una única vez y se puede dar de baja correctamente al desmontar.
	const selectedDeliveryNotesRef = React.useRef(selectedDeliveryNotes);
	React.useEffect(() => { selectedDeliveryNotesRef.current = selectedDeliveryNotes; });

	const searchTextRef = React.useRef<string>('');

	// No se usa useForceUpdate para forzar el remount del CheckBoxMenu de columnas,
	// ya que al cambiar el estado de showAllColumns o checkedColumns no se actualizan bien las columnas del componente de chakra en la UI.
	const [checkMenuColumnsKey, setCheckMenuColumnsKey] = React.useState<number>(0);
	const remountColumnsCheckMenu = React.useCallback(() => setCheckMenuColumnsKey(k => k + 1), []);

	// Visibilidad de columnas persistida en localStorage. La leemos una única vez al montar
	// para sembrar el estado inicial; si no hay nada guardado (primera vez) usamos los valores
	// por defecto de siempre (todas las columnas visibles).
	const persistedColumnsVisibility = React.useMemo(() => ChakraUtils.getColumnsVisibility('SHOW_DELIVERY_NOTES_COLUMNS_VISIBILITY_KEY'), []);

	// Estado de qué columnas están visibles en la tabla.
	// - `showAllColumns` = true  → se muestran todas las columnas y los checks individuales están desmarcados.
	// - `showAllColumns` = false → se muestran solo las columnas cuyo check individual está marcado.
	const [showAllColumns, setShowAllColumns] = React.useState<boolean>(persistedColumnsVisibility?.showAllColumns ?? true);
	const [checkedColumns, setCheckedColumns] = React.useState<Record<ChakraUtils.ColumnKey<DeliveryNoteModel>, boolean>>(() =>
		persistedColumnsVisibility?.checkedColumns
		?? columnsDef.reduce((acc, col) => { acc[col.key] = false; return acc; }, {} as Record<ChakraUtils.ColumnKey<DeliveryNoteModel>, boolean>)
	);

	// Cada vez que cambia la visibilidad de columnas (por interacción con el CheckBoxMenu),
	// la guardamos en localStorage para recuperarla en la siguiente apertura de la pantalla.
	React.useEffect(() => {
		ChakraUtils.saveColumnsVisibility({ showAllColumns, checkedColumns }, 'SHOW_DELIVERY_NOTES_COLUMNS_VISIBILITY_KEY');
	}, [showAllColumns, checkedColumns]);

	const clearCheckedColumns = React.useCallback(() => {
		const checkedColumns = columnsDef.reduce((acc, k) => { acc[k.key] = false; return acc; }, {} as Record<ChakraUtils.ColumnKey<DeliveryNoteModel>, boolean>);
		setCheckedColumns(checkedColumns);
	}, [columnsDef]);

	// Conjunto de columnas actualmente visibles: si "Todos" está marcado se muestran todas,
	// en caso contrario solo las que tengan su check individual activo.
	const visibleColumns = React.useMemo<Set<ChakraUtils.ColumnKey<DeliveryNoteModel>>>(() => {

		const allColumnsKeys = columnsDef.map(c => c.key);

		const checkedColumnsKeys = allColumnsKeys.filter(k => checkedColumns[k]);
		return new Set<ChakraUtils.ColumnKey<DeliveryNoteModel>>(showAllColumns ? allColumnsKeys : checkedColumnsKeys);
	}, [columnsDef, showAllColumns, checkedColumns]);

	const visibleDefs = columnsDef.filter(c => visibleColumns.has(c.key));

	// Handler para el check "Todos": al marcarlo, se muestran todas las columnas y se desmarcan los demás checks.
	// Al desmarcarlo, se mantiene el conjunto actual de checks individuales.
	const onAllCheckedChange = React.useCallback((checked: boolean) => {
		if (checked) {
			setShowAllColumns(true);
			clearCheckedColumns();
			remountColumnsCheckMenu();
		} else {
			setShowAllColumns(false);
		}
	}, [clearCheckedColumns, remountColumnsCheckMenu]);

	// Handler para cada check de columna: si se marca uno cuando "Todos" estaba activo,
	// se desmarca "Todos" para pasar a modo "solo las columnas marcadas".
	const onColumnCheckedChange = React.useCallback((column: ChakraUtils.ColumnKey<DeliveryNoteModel>, checked: boolean) => {
		setCheckedColumns(prev => ({ ...prev, [column]: checked }));
		if (checked && showAllColumns) {
			setShowAllColumns(false);
			remountColumnsCheckMenu();
		}
	}, [showAllColumns, remountColumnsCheckMenu]);

	const filterDeliveryNotes = React.useCallback((notes: DeliveryNoteModel[], searchText: string): DeliveryNoteModel[] => {
		const normalizedSearchText = searchText.trim().toLowerCase();
		if (!normalizedSearchText)
			return notes;

		const columns = buildColumnsDef();
		return notes.filter(note => columns.some(col => String(col.render(note) ?? '').toLowerCase().includes(normalizedSearchText)));
	}, []);

	// La previsualización es siempre la del primer albarán marcado. Si la selección no se puede facturar
	// junta no hay nada que previsualizar, así que ni pedimos la plantilla al servidor.
	const getPreview = React.useCallback(async (notes: DeliveryNoteModel[]): Promise<Nullable<PrintPreviewDTO>> => {
		if (notes.length === 0 || !canBeInvoicedTogether(notes))
			return null;

		return (await PrintFacade.getDeliveryNotePreviews(notes[0].Id))[0];
	}, []);

	// La búsqueda contra el servidor se mantiene como en la pantalla antigua (searchByText), ya que
	// busca también por datos que no son columna de la tabla (teléfono del cliente, por ejemplo).
	// El texto del buscador filtra la tabla en cliente mientras se escribe (onFilter) y sólo se manda
	// al servidor al pulsar la lupa (onSearch) o al recargar por un evento de dominio.
	const reload = React.useCallback(async (searchText: string) => {
		const notes = (await DeliveryNoteFacade.searchByText(searchText)).map(toModel);
		const selectedDeliveryNotes = notes.filter(x => selectedDeliveryNotesRef.current.some(selected => selected.Id === x.Id));
		const preview = await getPreview(selectedDeliveryNotes);

		setAllDeliveryNotes(notes);
		setDeliveryNotes(notes);
		setSelectedDeliveryNotes(selectedDeliveryNotes);
		setPreview(preview);
	}, [getPreview]);

	// La pantalla es multiselección: la previsualización y las opciones de un único albarán
	// trabajan siempre contra el primer albarán marcado.
	const onSelectDeliveryNotes = React.useCallback(async (selectedDeliveryNotes: DeliveryNoteModel[]) => {
		const preview = await getPreview(selectedDeliveryNotes);
		setSelectedDeliveryNotes(selectedDeliveryNotes);
		setPreview(preview);
	}, [getPreview]);

	// Marcar/desmarcar el check de una fila añade o quita ese albarán de la selección, manteniendo
	// siempre el orden de la tabla (así la previsualización es la del primer albarán marcado que se ve).
	const onToggleDeliveryNote = React.useCallback(async (note: DeliveryNoteModel, checked: boolean) => {
		const selectedIds = new Set(selectedDeliveryNotesRef.current.map(x => x.Id));

		if (checked)
			selectedIds.add(note.Id);
		else
			selectedIds.delete(note.Id);

		await onSelectDeliveryNotes(deliveryNotes.filter(x => selectedIds.has(x.Id)));
	}, [deliveryNotes, onSelectDeliveryNotes]);

	// Pulsar en la fila fuera del check deja seleccionado únicamente ese albarán (y su previsualización),
	// descartando el resto de checks que hubiera marcados.
	const onSelectOnlyDeliveryNote = React.useCallback(async (note: DeliveryNoteModel) => {
		await onSelectDeliveryNotes([note]);
	}, [onSelectDeliveryNotes]);

	// Check de la cabecera: marca todos los albaranes de la tabla o los desmarca todos.
	const onToggleAllDeliveryNotes = React.useCallback(async (checked: boolean) => {
		await onSelectDeliveryNotes(checked ? [...deliveryNotes] : []);
	}, [deliveryNotes, onSelectDeliveryNotes]);

	const onBarcodeRead = React.useCallback(async (code: string) => {

		const idResult = await BarcodeEncoder.tryDecodeId(BarcodeEncoder.EntityType.DeliveryNote, code);
		if (!idResult.ok) {
			await Message.show(BarcodeEncoder.isQRCode(code)
				? Res.Msg.MSG_QR_IS_NOT_A_VALID_PENDING_DELIVERY_NOTE_QR(code)
				: Res.Msg.MSG_BARCODE_IS_NOT_A_VALID_PENDING_DELIVERY_NOTE_BARCODE(code));
			return;
		}

		const deliveryNote = await DeliveryNoteFacade.getById(idResult.value);
		if (!deliveryNote || deliveryNote.Id === 0) {
			await Message.show(Res.Msg.MSG_DELIVERY_NOTE_NOT_FOUND());
			return;
		}

		const note = toModel(deliveryNote);
		const preview = await getPreview([note]);

		// Igual que en la pantalla antigua, al leer un código de barras la tabla se queda
		// únicamente con el albarán leído y ya marcado. Se recupera el listado completo al limpiar el buscador.
		formRef.current?.setValue('searchText', '');
		searchTextRef.current = '';
		setAllDeliveryNotes([note]);
		setDeliveryNotes([note]);
		setSelectedDeliveryNotes([note]);
		setPreview(preview);
	}, [getPreview]);

	React.useEffect(() => {
		const disposeReader = BarcodeReader.start(onBarcodeRead);

		const disposeOperationFailed = Events.on('pos:delivery-note:operation-failed', async ({ msg }) => {
			await Message.show(msg);
			await reload(searchTextRef.current);
		});

		const disposeChanged = Events.on('pos:delivery-note:changed', async () => await reload(searchTextRef.current));

		return () => {
			disposeReader();
			disposeOperationFailed();
			disposeChanged();
		};
	}, [onBarcodeRead, reload]);

	const onSearch = React.useCallback(async () => {
		const formValue = await formRef.current!.getValue();

		if (!formValue.ok)
			return;

		searchTextRef.current = formValue.value.searchText;
		await reload(formValue.value.searchText);
	}, [reload]);

	const onFilter = React.useCallback(async (value: string) => {
		const filteredDeliveryNotes = filterDeliveryNotes(allDeliveryNotes, value);
		setDeliveryNotes(filteredDeliveryNotes);

		// Igual que hacía el reload de la pantalla antigua con la búsqueda, la selección se queda sólo
		// con los albaranes que siguen visibles: así los totales, el check de la cabecera y las acciones
		// de varios albaranes siempre cuadran con lo que se está viendo en la tabla.
		const selectedIds = new Set(selectedDeliveryNotesRef.current.map(x => x.Id));
		const stillSelectedDeliveryNotes = filteredDeliveryNotes.filter(x => selectedIds.has(x.Id));

		if (stillSelectedDeliveryNotes.length !== selectedDeliveryNotesRef.current.length)
			await onSelectDeliveryNotes(stillSelectedDeliveryNotes);
	}, [allDeliveryNotes, filterDeliveryNotes, onSelectDeliveryNotes]);

	const onClear = React.useCallback(async () => {
		formRef.current?.setValue('searchText', '');
		searchTextRef.current = '';
		await reload('');
	}, [reload]);

	function columnCheckMenuItems(onAllCheckedChange: (checked: boolean) => void, onColumnCheckedChange: (column: ChakraUtils.ColumnKey<DeliveryNoteModel>, checked: boolean) => void, checkedColumns: Record<string, boolean>, showAllColumns: boolean): CheckBoxMenuItemDef[] {
		const checkMenuItems: CheckBoxMenuItemDef[] = [
			{ value: 'All', title: Res.Common.CHECK_BOX_MENU_ALL_OPTION_LBL(), checked: showAllColumns, onCheckedChange: onAllCheckedChange },
			{ value: 'SerieAndNumber', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_NUMBER_CLM(), checked: checkedColumns.SerieAndNumber, onCheckedChange: (checked) => onColumnCheckedChange('SerieAndNumber', checked) },
			{ value: 'CustomerDocumentNumber', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_CUSTOMER_DOCUMENT_NUMBER_CLM(), checked: checkedColumns.CustomerDocumentNumber, onCheckedChange: (checked) => onColumnCheckedChange('CustomerDocumentNumber', checked) },
			{ value: 'Customer', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_CUSTOMER_NAME_CLM(), checked: checkedColumns.Customer, onCheckedChange: (checked) => onColumnCheckedChange('Customer', checked) },
			{ value: 'Cif', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_CIF_CLM(), checked: checkedColumns.Cif, onCheckedChange: (checked) => onColumnCheckedChange('Cif', checked) },
			{ value: 'CreationDate', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_CREATION_DATE_CLM(), checked: checkedColumns.CreationDate, onCheckedChange: (checked) => onColumnCheckedChange('CreationDate', checked) },
			{ value: 'NetAmount', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_NET_AMOUNT_CLM(), checked: checkedColumns.NetAmount, onCheckedChange: (checked) => onColumnCheckedChange('NetAmount', checked) },
			{ value: 'PendingAmount', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_PENDING_AMOUNT_CLM(), checked: checkedColumns.PendingAmount, onCheckedChange: (checked) => onColumnCheckedChange('PendingAmount', checked) },
			{ value: 'PrepaidAmount', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_PREPAID_AMOUNT_CLM(), checked: checkedColumns.PrepaidAmount, onCheckedChange: (checked) => onColumnCheckedChange('PrepaidAmount', checked) },
			{ value: 'User', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_USER_CLM(), checked: checkedColumns.User, onCheckedChange: (checked) => onColumnCheckedChange('User', checked) },
			{ value: 'Location', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_LOCATION_CLM(), checked: checkedColumns.Location, onCheckedChange: (checked) => onColumnCheckedChange('Location', checked) },
			{ value: 'PosName', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_POS_CLM(), checked: checkedColumns.PosName, onCheckedChange: (checked) => onColumnCheckedChange('PosName', checked) },
			{ value: 'Notes', title: Res.Web.FIND_PENDING_DELIVERY_NOTES_NOTES_CLM(), checked: checkedColumns.Notes, onCheckedChange: (checked) => onColumnCheckedChange('Notes', checked) },
		];

		return checkMenuItems;
	}

	function buildColumnsDef(): ChakraUtils.ColumnDef<DeliveryNoteModel>[] {

		const columns: ChakraUtils.ColumnDef<DeliveryNoteModel>[] = [
			{ key: 'SerieAndNumber', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_NUMBER_CLM(), render: x => x.SerieAndNumber, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'CustomerDocumentNumber', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_CUSTOMER_DOCUMENT_NUMBER_CLM(), render: x => x.CustomerDocumentNumber, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'Customer', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_CUSTOMER_NAME_CLM(), render: x => x.Customer.FiscalName, compare: (a: BuyerCustomerDTO, b: BuyerCustomerDTO) => a.FiscalName.localeCompare(b.FiscalName) },
			{ key: 'Cif', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_CIF_CLM(), render: x => x.Cif, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'CreationDate', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_CREATION_DATE_CLM(), render: x => UI.Format.asDate(x.CreationDate), compare: (a: Date, b: Date) => new Date(a).getTime() - new Date(b).getTime() },
			{ key: 'NetAmount', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_NET_AMOUNT_CLM(), render: x => UI.Format.asMoney(x.NetAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'PendingAmount', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_PENDING_AMOUNT_CLM(), render: x => UI.Format.asMoney(x.PendingAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'PrepaidAmount', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_PREPAID_AMOUNT_CLM(), render: x => UI.Format.asMoney(x.PrepaidAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'User', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_USER_CLM(), render: x => x.User, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'Location', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_LOCATION_CLM(), render: x => x.Location, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'PosName', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_POS_CLM(), render: x => x.PosName, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'Notes', header: Res.Web.FIND_PENDING_DELIVERY_NOTES_NOTES_CLM(), render: x => x.Notes.replace(/\n/g, ' '), compare: (a: string, b: string) => a.localeCompare(b) }
		];

		return columns;
	};

	const onSortBy = React.useCallback((column: ChakraUtils.ColumnDef<DeliveryNoteModel>) => {
		if (!column.compare)
			return;

		const currentSortDirection = selectedColumn?.key === column.key ? columnSortDirection : null;
		const nextDirection: ChakraUtils.SortDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';

		setSelectedColumn(column);
		setColumnSortDirection(nextDirection);
		setDeliveryNotes(prevDeliveryNotes => ChakraUtils.sortRows(prevDeliveryNotes, column, nextDirection));
	}, [selectedColumn, columnSortDirection]);

	const showNotes = React.useCallback(async () => {
		if (selectedDeliveryNotes.length !== 0 && selectedDeliveryNotes[0].Notes)
			await Message.show(selectedDeliveryNotes[0].Notes);
	}, [selectedDeliveryNotes]);

	const reopen = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		const result = await Command.execute('reopenDeliveryNote', selected[0]);
		if (result.ok)
			close({ type: 'loadTicket', ticketId: result.value.reopenedTicketId });
	}, [selectedDeliveryNotes, close]);

	const showPayments = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		await Command.execute('showDeliveryNotePayments', selected[0]);
	}, [selectedDeliveryNotes]);

	const changeCustomer = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		await Command.execute('updateDeliveryNoteCustomer', selected[0]);
	}, [selectedDeliveryNotes]);

	const print = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		await Command.execute('printDeliveryNote', selected[0]);
	}, [selectedDeliveryNotes]);

	const send = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		await Command.execute('sendDeliveryNoteByEmail', selected[0]);
	}, [selectedDeliveryNotes]);

	const printCoupons = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		await Command.execute('printDeliveryNoteCoupons', selected[0]);
	}, [selectedDeliveryNotes]);

	const updateTableCompanions = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		const result = await getTableCompanions({
			FriendlyName: '',
			TableCompanions: selected[0].TableCompanions,
			Location: {
				SaleCenterName: '',
				SaleLocationName: ''
			}
		}, 9999);

		if (!result.ok)
			return;

		await execute({
			action: async () => {
				await DeliveryNoteStore.setTableCompanions(selected[0].Id, result.value);
			}
		});
	}, [selectedDeliveryNotes, execute]);

	// Opciones que trabajan con todos los albaranes marcados en la tabla.
	const printProform = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		const canBeInvoiced = await NotePreconditions.canBeInvoiced(selected);
		if (!canBeInvoiced)
			return;

		await Command.execute('printDeliveryNotesProform', selected);
	}, [selectedDeliveryNotes]);

	const cancelDeliveryNotes = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		await Command.execute('cancelDeliveryNotes', selected);
	}, [selectedDeliveryNotes]);

	const pay = React.useCallback(async () => {
		const selected = selectedDeliveryNotes;
		if (selected.length === 0)
			return;

		if (selected.some(x => x.Customer.ApplySurcharge !== selected[0].Customer.ApplySurcharge)) {
			await Message.show(Res.Msg.MSG_CANNOT_PROCESS_DELIVERY_NOTE_WITH_CUSTOMERS_WITH_DIFFERENT_SURCHAGE_OPTION());
			return;
		}

		await Command.execute('createInvoiceOrRefundDeliveryNotes', selected);
	}, [selectedDeliveryNotes]);

	const onShowNotes = useClickWithFeedback(() => showNotes());
	const onReopen = useClickWithFeedback(() => reopen());
	const onShowPayments = useClickWithFeedback(() => showPayments());
	const onChangeCustomer = useClickWithFeedback(() => changeCustomer());
	const onPrint = useClickWithFeedback(() => print());
	const onSend = useClickWithFeedback(() => send());
	const onPrintCoupons = useClickWithFeedback(() => printCoupons());
	const onUpdateTableCompanions = useClickWithFeedback(() => updateTableCompanions());
	const onPrintProform = useClickWithFeedback(() => printProform());
	const onCancelDeliveryNotes = useClickWithFeedback(() => cancelDeliveryNotes());
	const onPay = useClickWithFeedback(() => pay());

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	// Como en la pantalla antigua: si hay albaranes marcados los totales se calculan sobre la
	// selección, y si no hay ninguno sobre lo que se está viendo en la tabla (ya filtrado).
	const summaryDeliveryNotes = selectedDeliveryNotes.length ? selectedDeliveryNotes : deliveryNotes;

	// Si hay varios albaranes marcados que no se pueden facturar juntos no tiene sentido previsualizar
	// ninguno de ellos, así que en lugar de la plantilla mostramos el aviso correspondiente.
	const selectionCanBeInvoicedTogether = canBeInvoicedTogether(selectedDeliveryNotes);

	// Estado del check de la cabecera: marcado si están todos los albaranes de la tabla marcados
	// e indeterminado ('-') mientras sólo haya marcados algunos.
	const selectedDeliveryNoteIds = new Set(selectedDeliveryNotes.map(x => x.Id));
	const allDeliveryNotesSelected = deliveryNotes.length > 0 && selectedDeliveryNoteIds.size === deliveryNotes.length;
	const someDeliveryNotesSelected = selectedDeliveryNoteIds.size > 0 && selectedDeliveryNoteIds.size < deliveryNotes.length;

	return <PosScreen screenId='SHOW_DELIVERY_NOTES' size='full-screen' useKeyPad={false}>
		<Header title={Res.Web.FIND_PENDING_DELIVERY_NOTES()} image={Images.ShowDeliveryNotes} onClose={cancel} />
		<Content>
			<VStack width={'full'} height={'full'} overflow={'hidden'}>
				<VStack width={'full'} height={'full'} gap={'0'} overflow={'hidden'}>
					<HStack width={'full'} paddingX={'6'} paddingY={'3'} textAlign={'center'} justifyContent={'space-between'} gap={'20'} borderBottom={'1px solid'} borderColor={'neutral.300'}>
						<Box width={'58'} height={'8'} bg={{ base: '{colors.blue.100}', _dark: '{colors.blue.800}' }} borderRadius={'md'} padding={'0.5'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.700}', _dark: 'white' }}>
								{Res.Web.FIND_PENDING_DELIVERY_NOTES_NET_AMOUNT_AMOUNT_LBL(UI.Format.asMoney(summaryDeliveryNotes.reduce((sum, x) => sum + x.NetAmount, 0)))}
							</Text>
						</Box>
						<Box width={'58'} height={'8'} bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' }} borderRadius={'md'} padding={'0.5'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.700}', _dark: 'white' }}>
								{Res.Web.FIND_PENDING_DELIVERY_NOTES_PENDING_AMOUNT_LBL(UI.Format.asMoney(summaryDeliveryNotes.reduce((sum, x) => sum + x.PendingAmount, 0)))}
							</Text>
						</Box>
						<Box width={'58'} height={'8'} bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' }} borderRadius={'md'} padding={'0.5'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.700}', _dark: 'white' }}>
								{Res.Web.FIND_PENDING_DELIVERY_NOTES_PREPAID_AMOUNT_LBL(UI.Format.asMoney(summaryDeliveryNotes.reduce((sum, x) => sum + x.PrepaidAmount, 0)))}
							</Text>
						</Box>
					</HStack>
					<HStack width={'full'} height={'full'} padding={'6'} gap={'6'} overflow={'hidden'}>
						<VStack w={'stretch'} height={'full'} gap={'9'} overflow={'hidden'}>
							<HStack width={'full'} minH={'18'}>
								<Form ref={formRef} initialValue={initialFormState} schema={stateSchema} style={{ width: '100%' }}>
									<HStack width={'full'} gap={'9'} justifyContent={'flex-start'}>
										<Box width={'stretch'} maxW={'96'}>
											<Input
												linkTo={'searchText'}
												placeholder={Res.Web.FIND_PENDING_DELIVERY_NOTES_SEARCH_PLACEHOLDER()}
												onWatch={(value) => onFilter(value ?? '')}
												startElement={<SvgIcon src={Images.Search} width={'4'} height={'4'} cursor={'pointer'} onClick={onSearch} />}
												endElement={<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />}
												borderRadius={'4xl'}
												borderColor={'neutral.600'}
												height={'10'}
												onKeyDown={UI.whenEnterKey(onSearch)}
											/>
										</Box>
										{/* Acciones que se aplican sobre todos los albaranes marcados en la tabla */}
										{selectedDeliveryNotes.length > 0 &&
											<HStack gap={'2'} alignItems={'flex-start'} justifyContent={'flex-start'}>
												<VStack width={'13'} gap={'1'} {...onCancelDeliveryNotes} cursor={'pointer'}>
													<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
														<SvgIcon src={Images.CancelDeliveryNote} width={'6'} height={'6'} />
													</Box>
													<Box paddingX={'2'}>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_CANCEL_NOTE_BTN()}</Text>
													</Box>
												</VStack>
												<VStack width={'13'} gap={'1'} {...onPrintProform} cursor={'pointer'}>
													<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
														<SvgIcon src={Images.PrintInvoice} width={'6'} height={'6'} />
													</Box>
													<Box paddingX={'2'}>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_PRINT_PROFORM_INVOICE_BTN()}</Text>
													</Box>
												</VStack>
												<VStack width={'13'} gap={'1'} {...onPay} cursor={'pointer'}>
													<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
														<SvgIcon src={Images.CloseTicket} width={'6'} height={'6'} />
													</Box>
													<Box paddingX={'2'}>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_PAY_BTN()}</Text>
													</Box>
												</VStack>
											</HStack>
										}
										<HStack>
											<SvgIcon src={Images.Columns} width={'6'} height={'6'} />
											<CheckBoxMenu
												key={checkMenuColumnsKey}
												items={columnCheckMenuItems(
													onAllCheckedChange,
													onColumnCheckedChange,
													checkedColumns,
													showAllColumns
												)}
												icon={<SvgIcon src={Images.ChevronDown} width={'5'} height={'5'} />}
											/>
										</HStack>
									</HStack>
								</Form>
							</HStack>
							<VStack width={'full'} height={'full'} gap={'4'} overflow={'hidden'}>
								<VStack width={'full'} height={'stretch'}>
									<HStack width={'full'} justifyContent={'flex-start'}>
										<Box minW={'4'} minH={'4'}><SvgIcon src={Images.SortByDesc} width={'4'} height={'4'} /></Box>
										<Box width={'full'}><Text fontSize={'2xs'}>{Res.Common.SORT_TABLE_COLUMNS_PLACEHOLDER()}</Text></Box>
									</HStack>
									<HStack overflow={'auto'} minW={'full'} maxW={'stretch'} height={'full'} minH={'0'} maxH={'stretch'} alignItems={'flex-start'}>
										<TableScrollArea maxWidth={'stretch'} minWidth={'full'} maxHeight={'stretch'} rounded={'md'}>
											<Table.Root size={'sm'} striped stickyHeader interactive>
												<Table.Header>
													<Table.Row>
														{/* Columna de selección: la pantalla es multiselección (cobrar, cancelar o imprimir proforma de varios albaranes) */}
														<Table.ColumnHeader width={'10'}>
															<Checkbox.Root
																size={'sm'}
																colorPalette={'blue'}
																checked={someDeliveryNotesSelected ? 'indeterminate' : allDeliveryNotesSelected}
																onCheckedChange={(changes) => onToggleAllDeliveryNotes(changes.checked === true)}
																cursor={'pointer'}
															>
																<Checkbox.HiddenInput />
																<Checkbox.Control>
																	<Checkbox.Indicator />
																</Checkbox.Control>
															</Checkbox.Root>
														</Table.ColumnHeader>
														{visibleDefs.map((col) => (
															<Table.ColumnHeader key={col.key} onClick={() => onSortBy(col)} cursor={'pointer'} textAlign={col.textAlign}>
																<HStack gap={'0.5'} justifyContent={'flex-start'}>
																	<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{col.header}</Text>
																	{selectedColumn?.key === col.key && <SvgIcon src={columnSortDirection === 'asc' ? Images.SortByDesc : Images.SortByAsc} width={'4'} height={'4'} />}
																</HStack>
															</Table.ColumnHeader>
														))}
													</Table.Row>
												</Table.Header>
												<Table.Body>
													{(deliveryNotes.length === 0 || columnsDef.length === 0) && (
														<Table.Row>
															<Table.Cell colSpan={Math.max(visibleDefs.length + 1, 1)} textAlign={'center'}>
																<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.FIND_PENDING_DELIVERY_NOTES_SEARCH_NO_RESULTS()}</Text>
															</Table.Cell>
														</Table.Row>
													)}
													{deliveryNotes.length > 0 && deliveryNotes.map((note) => {
														const isSelected = selectedDeliveryNoteIds.has(note.Id);
														const cellBackGround: TableCellProps['bg'] = isSelected ? { base: '{colors.blue.100}', _dark: '{colors.blue.800}' } : undefined;

														return (
															<Table.Row
																key={note.Id}
																data-selected={isSelected ? '' : undefined}
																onClick={() => onSelectOnlyDeliveryNote(note)}
																cursor={'pointer'}
															>
																{/* El click del check no debe propagarse a la fila: el check suma/resta a la selección
																	mientras que el click en la fila selecciona sólo ese albarán. */}
																<Table.Cell background={cellBackGround} width={'10'} onClick={(e) => e.stopPropagation()}>
																	<Checkbox.Root
																		size={'sm'}
																		colorPalette={'blue'}
																		checked={isSelected}
																		onCheckedChange={(changes) => onToggleDeliveryNote(note, changes.checked === true)}
																		cursor={'pointer'}
																	>
																		<Checkbox.HiddenInput />
																		<Checkbox.Control>
																			<Checkbox.Indicator />
																		</Checkbox.Control>
																	</Checkbox.Root>
																</Table.Cell>
																{visibleDefs.map(col => (
																	<Table.Cell background={cellBackGround} key={col.key} textAlign={col.textAlign}>
																		<Text fontSize={'sm'}>{col.render(note)}</Text>
																	</Table.Cell>
																))}
															</Table.Row>
														);
													})}
												</Table.Body>
											</Table.Root>
										</TableScrollArea>
									</HStack>
								</VStack>
							</VStack>
						</VStack>
						<VStack minW={'95'} height={'full'} gap={'4'} overflow={'hidden'}>
							{!selectionCanBeInvoicedTogether ? (
								<VStack justifyContent={'center'} alignItems={'center'} minW={'95'} height={'full'}>
									<SvgIcon src={Images.RecentDeliveryNotes} width={'16'} height={'16'} />
									<Text fontSize={'lg'} fontWeight={'semibold'} textAlign={'center'} color={{ base: 'secondary', _dark: 'white' }}>{Res.Web.FIND_PENDING_DELIVERY_NOTES_PREVIEW_NOT_AVAILABLE_LBL()}</Text>
								</VStack>
							) : preview === null ? (
								<VStack justifyContent={'center'} alignItems={'center'} minW={'95'} height={'full'}>
									<SvgIcon src={Images.RecentDeliveryNotes} width={'16'} height={'16'} />
									<Text fontSize={'lg'} fontWeight={'semibold'} textAlign={'center'} color={{ base: 'secondary', _dark: 'white' }}>{Res.Web.FIND_PENDING_DELIVERY_NOTES_SELECTION_DELIVERY_NOTE_TO_PREVIEW_LBL()}</Text>
								</VStack>
							) : (
								<>
									{/* Acciones que se aplican sobre el albarán previsualizado (el primero de los marcados).
										El orden es el del diseño: Entradas, Reabrir, Cambiar Comensales, Enviar, Gestionar Pagos y Cliente.
										Imprimir Albarán y Notas se quedan en el menú "Más" para no perder ninguna opción de la pantalla antigua. */}
									<HStack gap={'0'} alignItems={'flex-start'} width={'full'} justifyContent={'space-evenly'}>
										<VStack width={'13'} gap={'1'} {...onPrintCoupons} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.Tickets} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_PRINT_COUPONS_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onReopen} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.Refresh} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_REOPEN_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onUpdateTableCompanions} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.TableCompanions} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_TABLE_COMPANIONS_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onSend} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.Send} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_SEND_EMAIL_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onShowPayments} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.ShowPayments} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_SHOW_PAYMENTS_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onChangeCustomer} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.User} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.PENDING_DELIVERY_NOTES_UPDATE_CUSTOMER_DATA_BTN()}</Text>
											</Box>
										</VStack>
										<Separator orientation='vertical' height={'stretch'} size={'xs'} border={'1px solid'} borderColor={'neutral.300'} marginX={'1'} />
										<Menu.Root>
											<Menu.Trigger padding={'0'} height={'stretch'} _hover={{ background: 'none' }} border={'none'} alignItems={'flex-start'} asChild>
												<Button variant="outline" size="sm">
													<VStack height={'full'}>
														<Box padding={'2'}>
															<SvgIcon src={Images.JustPointMore} width={'6'} height={'6'} />
														</Box>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Common.MORE_BTN()}</Text>
													</VStack>
												</Button>
											</Menu.Trigger>
											<Portal>
												<Menu.Positioner>
													<Menu.Content>
														<Menu.Item value={'print'} {...onPrint} cursor={'pointer'}>{Res.Web.PENDING_DELIVERY_NOTES_PRINT_DELIVERY_NOTE_BTN()}</Menu.Item>
														{selectedDeliveryNotes.length !== 0 && selectedDeliveryNotes[0].Notes &&
															<Menu.Item value={'notes'} {...onShowNotes} cursor={'pointer'}>{Res.Web.PENDING_DELIVERY_NOTES_NOTES_BTN()}</Menu.Item>
														}
													</Menu.Content>
												</Menu.Positioner>
											</Portal>
										</Menu.Root>
									</HStack>
									<VStack justifyContent={'flex-start'} alignItems={'flex-start'} overflowY={'auto'} overflowX={'hidden'}>
										<UI.TemplatePrintPreview preview={preview} />
									</VStack>
								</>
							)}
						</VStack>
					</HStack>
				</VStack>
			</VStack>
		</Content >
		<Footer>
			<BackActionButton action={onCancel} />
		</Footer>
	</PosScreen >;
}


declare global {
	interface CommandType {
		showDeliveryNotes: () => Promise<Result<SaleModuleNextAction>>
	}
}

Command.register('showDeliveryNotes', Preconditions.and(Preconditions.ensure(Permission.DeliveryNote_FindPending), Preconditions.ensureIsNotLite), async (): Promise<Result<SaleModuleNextAction>> => {
	const initialDeliveryNotes = (await DeliveryNoteFacade.searchByText('')).map(toModel);
	return Modal.show(ShowDeliveryNotesScreen, { initialDeliveryNotes: initialDeliveryNotes });
});
